import { useEffect, useState } from "react";
import { api, formatEUR } from "../lib/apiClient";
import { ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";

export default function Dashboard() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/billing/summary")
      .then(({ data }) => setSummary(data))
      .catch(() => setSummary(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-8 font-mono text-sm text-zinc-500">Cargando…</div>;

  const budget = summary?.budget;
  const inv = summary?.invoices;

  return (
    <div className="p-6 md:p-10 max-w-7xl" data-testid="dashboard-page">
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="label-eyebrow">Panel general</p>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tight mt-2">Resumen</h1>
        </div>
      </div>

      {budget && budget.total > 0 && (
        <section className="mt-2" data-testid="budget-summary">
          <div className="flex items-end justify-between mb-4">
            <div>
              <p className="label-eyebrow">Presupuesto cliente</p>
              <h2 className="font-heading text-2xl mt-1">Resumen general <span className="text-sm text-zinc-500 font-normal">(IVA no incluido)</span></h2>
            </div>
            <Link to="/presupuesto" className="text-xs uppercase tracking-wider text-zinc-500 hover:text-zinc-900 flex items-center gap-1">
              Ver presupuesto <ChevronRight className="h-3 w-3" strokeWidth={1.5} />
            </Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-zinc-900 text-white p-6" data-testid="budget-total">
              <p className="label-eyebrow text-zinc-400">Total presupuesto</p>
              <p className="font-heading font-mono text-3xl mt-2">{formatEUR(budget.total)}</p>
            </div>
            <div className="bg-white border border-zinc-200 p-6" data-testid="budget-area-restaurantes">
              <p className="label-eyebrow">Total restaurantes</p>
              <p className="font-heading font-mono text-2xl mt-2">{formatEUR(budget.by_area.restaurantes || 0)}</p>
              <p className="text-xs text-zinc-500 mt-1">Menús · {budget.total ? Math.round((budget.by_area.restaurantes || 0) / budget.total * 100) : 0}%</p>
            </div>
            <div className="bg-white border border-zinc-200 p-6" data-testid="budget-area-gestion">
              <p className="label-eyebrow">Total gestión</p>
              <p className="font-heading font-mono text-2xl mt-2">{formatEUR(budget.by_area.gestion || 0)}</p>
              <p className="text-xs text-zinc-500 mt-1">Acuerdos, preprod, personal · {budget.total ? Math.round((budget.by_area.gestion || 0) / budget.total * 100) : 0}%</p>
            </div>
            <div className="bg-white border border-zinc-200 p-6" data-testid="budget-area-decoracion">
              <p className="label-eyebrow">Total decoración</p>
              <p className="font-heading font-mono text-2xl mt-2">{formatEUR(budget.by_area.decoracion || 0)}</p>
              <p className="text-xs text-zinc-500 mt-1">Materiales · {budget.total ? Math.round((budget.by_area.decoracion || 0) / budget.total * 100) : 0}%</p>
            </div>
          </div>
        </section>
      )}

      {inv && inv.totals.total_to_pay > 0 && (
        <section className="mt-10" data-testid="invoices-summary">
          <div className="flex items-end justify-between mb-4">
            <div>
              <p className="label-eyebrow">A pagar a restaurantes</p>
              <h2 className="font-heading text-2xl mt-1">Compromiso financiero <span className="text-sm text-zinc-500 font-normal">(IVA incluido)</span></h2>
            </div>
            <Link to="/invoices" className="text-xs uppercase tracking-wider text-zinc-500 hover:text-zinc-900 flex items-center gap-1">
              Ver facturas <ChevronRight className="h-3 w-3" strokeWidth={1.5} />
            </Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-zinc-900 text-white p-6" data-testid="inv-total">
              <p className="label-eyebrow text-zinc-400">Total a pagar</p>
              <p className="font-heading font-mono text-3xl mt-2">{formatEUR(inv.totals.total_to_pay)}</p>
              <p className="text-xs text-zinc-400 mt-1">Anticipo + Principal</p>
            </div>
            <div className="bg-white border border-zinc-200 p-6" data-testid="inv-anticipo">
              <p className="label-eyebrow">Anticipos (10%)</p>
              <p className="font-heading font-mono text-2xl mt-2">{formatEUR(inv.totals.anticipo)}</p>
              <p className="text-xs text-zinc-500 mt-1">1 mes antes del 1er evento</p>
            </div>
            <div className="bg-white border border-zinc-200 p-6" data-testid="inv-principal">
              <p className="label-eyebrow">Principal (90%)</p>
              <p className="font-heading font-mono text-2xl mt-2">{formatEUR(inv.totals.principal)}</p>
              <p className="text-xs text-zinc-500 mt-1">15 días antes del 1er evento</p>
            </div>
            <div className="bg-purple-50 border border-purple-200 p-6" data-testid="inv-nonshow">
              <p className="label-eyebrow text-purple-700">Non-show potencial</p>
              <p className="font-heading font-mono text-2xl mt-2 text-purple-800">{formatEUR(inv.totals.non_show_potential)}</p>
              <p className="text-xs text-purple-700 mt-1">Si se llenan todos los aforos</p>
            </div>
          </div>

          <div className="bg-white border border-zinc-200" data-testid="inv-by-rest">
            <div className="grid grid-cols-12 px-6 py-3 border-b border-zinc-200 text-xs uppercase tracking-wider text-zinc-500 font-semibold bg-zinc-50">
              <div className="col-span-3">Restaurante</div>
              <div className="col-span-1 text-right">Confirm.</div>
              <div className="col-span-1 text-right">Nuevos</div>
              <div className="col-span-1 text-right">Pte. confirmar</div>
              <div className="col-span-2 text-right">Anticipo</div>
              <div className="col-span-2 text-right">Principal</div>
              <div className="col-span-1 text-right">Non-show</div>
              <div className="col-span-1 text-right">Total</div>
            </div>
            {inv.by_restaurant.length === 0 && (
              <div className="p-6 text-sm text-zinc-500 text-center">Sin datos de facturación todavía.</div>
            )}
            {inv.by_restaurant.map((r) => {
              const ant = r.confirmed.anticipo + r.confirmed_late.anticipo;
              const pri = r.confirmed.principal + r.confirmed_late.principal;
              const provEv = r.provisional?.events || 0;
              return (
                <Link to={`/restaurants/${r.restaurant_id}`} key={r.restaurant_id} className="grid grid-cols-12 px-6 py-3 border-b border-zinc-100 items-center text-sm hover:bg-zinc-50" data-testid={`inv-by-rest-${r.restaurant_id}`}>
                  <div className="col-span-3">
                    <p className="font-medium">{r.name}</p>
                    <p className="text-xs text-zinc-500">{r.city}</p>
                  </div>
                  <div className="col-span-1 text-right font-mono text-xs">
                    <span className="px-1.5 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200">{r.confirmed.events}</span>
                  </div>
                  <div className="col-span-1 text-right font-mono text-xs">
                    {r.confirmed_late.events > 0 ? (
                      <span className="px-1.5 py-0.5 bg-purple-50 text-purple-700 border border-purple-200">{r.confirmed_late.events}</span>
                    ) : <span className="text-zinc-400">—</span>}
                  </div>
                  <div className="col-span-1 text-right font-mono text-xs" data-testid={`prov-${r.restaurant_id}`}>
                    {provEv > 0 ? (
                      <span className="px-1.5 py-0.5 bg-amber-50 text-amber-700 border border-amber-200">{provEv}</span>
                    ) : <span className="text-zinc-400">—</span>}
                  </div>
                  <div className="col-span-2 text-right font-mono">{formatEUR(ant)}</div>
                  <div className="col-span-2 text-right font-mono">{formatEUR(pri)}</div>
                  <div className="col-span-1 text-right font-mono text-xs text-purple-700">{formatEUR(r.non_show_potential)}</div>
                  <div className="col-span-1 text-right font-mono font-medium">{formatEUR(r.total_to_pay)}</div>
                </Link>
              );
            })}
            <div className="grid grid-cols-12 px-6 py-3 bg-zinc-900 text-white text-sm" data-testid="inv-by-rest-totals">
              <div className="col-span-3 label-eyebrow text-zinc-400">Totales</div>
              <div className="col-span-1"></div>
              <div className="col-span-1"></div>
              <div className="col-span-1 text-right font-mono text-amber-300">{inv.totals.provisional_events || 0}</div>
              <div className="col-span-2 text-right font-mono">{formatEUR(inv.totals.anticipo)}</div>
              <div className="col-span-2 text-right font-mono">{formatEUR(inv.totals.principal)}</div>
              <div className="col-span-1 text-right font-mono text-purple-300">{formatEUR(inv.totals.non_show_potential)}</div>
              <div className="col-span-1 text-right font-mono font-heading text-base">{formatEUR(inv.totals.total_to_pay)}</div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
